<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const props = defineProps({
  labels: { type: Array, required: true },
  datasets: { type: Array, required: true }, // [{ label, data, backgroundColor }]
  height: { type: Number, default: 240 },
})

const canvasRef = ref(null)
let chartInstance = null

function themeColor(varName, fallback) {
  if (typeof window === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return value || fallback
}

function render() {
  if (!canvasRef.value) return
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }

  const gridColor = themeColor('--border-subtle', 'rgba(0,0,0,0.12)')
  const textColor = themeColor('--text-secondary', 'rgba(0,0,0,0.6)')

  chartInstance = new Chart(canvasRef.value, {
    type: 'bar',
    data: { labels: props.labels, datasets: props.datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: props.datasets.length > 1, labels: { color: textColor } },
      },
      scales: {
        x: { ticks: { color: textColor }, grid: { color: gridColor } },
        y: { beginAtZero: true, ticks: { precision: 0, color: textColor }, grid: { color: gridColor } },
      },
    },
  })
}

onMounted(render)
onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})
watch(() => [props.labels, props.datasets], render, { deep: true })
</script>

<template>
  <div class="bar-chart-wrap" :style="{ height: `${height}px` }">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<style scoped>
.bar-chart-wrap {
  position: relative;
  width: 100%;
}
</style>
